/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2026 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2023-2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import React from 'react';
import { getAntiforgeryToken } from 'api/antiforgery';
import Footer from 'components/footer/footer';
import Header from 'components/header/header';
import PubSubDetail from 'components/PubSubDetail';
import TestValue from 'components/PubSubMachine/TestValue';
import SubscribedDetail from 'components/SubscribedDetail';
import Tabs from 'components/tabs/tabWrapper';
import TvView from 'components/tvView/tvView';
import WorldMap from 'components/worldMap/worldMap';
import { useEffect, useState } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { updateFaultyMachines, updateOnlineMachines } from 'redux/reducers/machines';
import ENVs from 'utilities/ENVs';
import MQTTComponent from 'utilities/MQTTComponent';
import MqttTopics from 'utilities/MqttTopics';
import MQTTWebsocket, { isTopicMatch } from 'utilities/mqtt/MQTTWebsocket';
import MqttProviderV3 from 'utilities/mqtt3/MqttConnection';
import OpcUaMqttPubSub from 'utilities/PubSub/OpcUaMqttPubSub';
import SubscriptionComponent from 'utilities/SubscriptionComponent';
import URLs from 'utilities/URLs';
import About from 'views/about';
import SoftwareDetailView from 'views/softwareDetail';
import { store } from './redux/store';
import DataPolicy from './views/datapolicy';
import FaultyList from './views/faultyMachineList';
import Machines from './views/machineList';
import Mixed from './views/mixedList';
import Software from './views/softwareList';
import EnergyTestValue from 'components/PubSubMachine/EnergyTestValue';
import VerboseTestValue from 'components/PubSubMachine/VerboseTestValue';
import { fetchUser } from 'redux/reducers/user';
import Account from 'views/account';
import Login from 'views/login';

function App() {
  const brokerUrl = URLs.MqttBrokerUrl();
  const [state] = useState({
    machineIdentification: null,
    fairs: {},
    machines: [],
    startReceivingMqttMessages: true,
    selectedTab: '',
    ws: new MQTTWebsocket(brokerUrl, ENVs.MqttUsername(), ENVs.MqttPassword(), 0),
  });

  useEffect(() => {
    getAntiforgeryToken();
    store.dispatch(fetchUser);
  }, []);

  const updateOnlineMachinesStore = (onlineMachines, topic) => {
    if (topic !== MqttTopics.MachinesOnline()) {
      console.error('Invalid Topic: ', topic);
    }
    store.dispatch(updateOnlineMachines(onlineMachines));
  };

  const updateFaultyMachineStore = (newFaultyMachines, topic) => {
    if (!isTopicMatch(MqttTopics.FaultyList('errors'), topic)) {
      console.error('Invalid Topic: ', topic);
    }
    store.dispatch(updateFaultyMachines(newFaultyMachines, topic));
  };

  return (
    <MQTTComponent ws={state.ws}>
      {/* <SubscriptionComponent
        topic={MqttTopics.List('#')}
        subscriptionCallback={updateMachineStore}
        startReceivingData={state.startReceivingMqttMessages}
      ></SubscriptionComponent> */}
      <SubscriptionComponent
        topic={MqttTopics.MachinesOnline()}
        subscriptionCallback={updateOnlineMachinesStore}
        startReceivingData={state.startReceivingMqttMessages}
      ></SubscriptionComponent>
      <SubscriptionComponent
        topic={MqttTopics.FaultyList('errors')}
        subscriptionCallback={updateFaultyMachineStore}
        startReceivingData={state.startReceivingMqttMessages}
      ></SubscriptionComponent>
      <MqttProviderV3 brokerUrl={URLs.MqttBrokerUrl()} username={ENVs.MqttUsername()} password={ENVs.MqttPassword()}>
        <Router>
          <Routes>
            <Route
              path="v3debug"
              element={
                <OpcUaMqttPubSub topicPath="isw/gateway/BasicMachineTool">
                  <TestValue />
                </OpcUaMqttPubSub>
              }
            />
            <Route
              path="v3Energy"
              element={
                <OpcUaMqttPubSub topicPath="vdw/server-node-dev/SampleMachineTool_Energy">
                  <EnergyTestValue />
                </OpcUaMqttPubSub>
              }
            />
            <Route
              path="v3verbose"
              element={
                <OpcUaMqttPubSub topicPath="verbose/server-cpp-dev/ShowcaseMachineTool">
                  <VerboseTestValue />
                </OpcUaMqttPubSub>
              }
            />
            <Route path="tvview/:fair" element={<TvView />} />
            <Route path="tvview/:fair/:slide" element={<TvView />} />
            <Route path="tvview/:fair/:slide/:machine" element={<TvView />} />
            <Route path=":fairTag/:typeId/:machine" element={<SubscribedDetail />} />
            <Route path="v3/:fairTag/:typeId/:machine" element={<PubSubDetail />} />

            <Route path="login" element={<Login />} />
            <Route path="account" element={<Account />} />

            <Route
              path="software/:fair/:software"
              element={
                <main className="footer-wrapper">
                  <SoftwareDetailView />
                </main>
              }
            />
            <Route
              path=":selected/:fair"
              element={
                <main className="footer-wraper">
                  <Header fairTag="all" containerClass="container-max" />
                  <Tabs
                    default="machines"
                    paramToAppendToLinks="fair"
                    tabElements={[
                      {
                        id: 'overview',
                        node: <Mixed />,
                        link: '/overview',
                      },
                      {
                        icon: 'icon-machines',
                        id: 'machines',
                        node: <Machines />,
                        link: '/machines',
                      },
                      {
                        icon: 'icon-software',
                        id: 'software',
                        node: <Software />,
                        link: '/software',
                      },
                    ]}
                  />
                  <Footer />
                </main>
              }
            />
            <Route
              path="about"
              element={
                <main className="footer-wrapper">
                  <Header fairTag="all" containerClass="container-max" />
                  <About />
                  <Footer />
                </main>
              }
            />
            <Route
              path="debug"
              element={
                <main className="footer-wrapper">
                  <Header fairTag="all" containerClass="container-max" />
                  <FaultyList />
                  <Footer />
                </main>
              }
            />
            <Route
              path="data-policy"
              element={
                <main className="footer-wrapper">
                  <Header fairTag="all" containerClass="container-max" />
                  <DataPolicy />
                  <Footer />
                </main>
              }
            />
            <Route
              path="/"
              element={
                <>
                  <Header fixed overview fairTag="all" containerClass="container-max" />
                  <div className="view-map">
                    <WorldMap />
                  </div>
                  <Footer fixed />
                </>
              }
            />
          </Routes>
        </Router>
      </MqttProviderV3>
    </MQTTComponent>
  );
}
export default App;
